import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Company } from 'src/types';
import { LIST_KEY, MESSAGING_COMPANY_LOCALE_QUERY_KEY, MESSAGING_QUERY_KEY, POST_KEY, PUT_KEY } from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from '../queryYemboApiCalls';
import { MessagingTemplate } from './messagingTypes';

const url = 'messaging';

type PartialMessagingTemplate = Partial<MessagingTemplate>;
type MessagingQueryOptions = Omit<
  UseQueryOptions<MessagingTemplate[] | null, Error, MessagingTemplate[]>,
  'queryKey' | 'queryFn'
>;
type PutMessagingOptions = UseMutationOptions<
  void,
  Error,
  PartialMessagingTemplate,
  {
    previous: PartialMessagingTemplate | undefined;
  }
>;

type PostCreateMessagingForNewLocaleOptions = UseMutationOptions<
  void,
  Error,
  { companyKey: string; locale: string },
  {
    previous: { companyKey: string; locale: string } | undefined;
  }
>;

const getMessagingData = async (companyKey: Company['key']) =>
  await queryYemboApiCallWithReturnValue<MessagingTemplate[] | null, 'messaging'>({
    url,
    key: 'messaging',
    urlParams: new URLSearchParams({ companyKey }),
    shouldNotifyAndThrowError: true,
  });

export const useMessagingQuery = (companyKey: Company['key'], options?: MessagingQueryOptions) =>
  useQuery({
    queryKey: [MESSAGING_QUERY_KEY, LIST_KEY, companyKey],
    queryFn: async () => getMessagingData(companyKey),
    ...options,
    enabled: !!companyKey && options?.enabled !== false,
  });

export const useMessagingCompanyAvailableLocales = (
  companyKey: Company['key'],
  options?: UseQueryOptions<string[] | null, Error, string[]>
) =>
  useQuery({
    queryKey: [MESSAGING_COMPANY_LOCALE_QUERY_KEY, LIST_KEY, companyKey],
    enabled: !!companyKey,
    queryFn: async () => [],
    ...options,
  });

const putMessage = async (template: PartialMessagingTemplate): Promise<void> => {
  await queryYemboApiCall({
    url,
    method: 'PUT',
    body: JSON.stringify(template),
  });
};

const postCreateMessagingForNewLocale = async (data: { companyKey: string; locale: string }): Promise<void> => {
  await queryYemboApiCall({
    url: `${url}/create-for-new-locale`,
    method: 'POST',
    body: JSON.stringify(data),
    shouldNotifyAndThrowError: true,
  });
};

export const usePutMessaging = (companyKey: Company['key'] | undefined, options?: PutMessagingOptions) => {
  const queryClient = useQueryClient();

  const queryKey = [MESSAGING_QUERY_KEY, LIST_KEY, companyKey];
  const mutationKey = [MESSAGING_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    ...options,
    mutationFn: putMessage,
    onMutate: async (template) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<MessagingTemplate[]>(queryKey);
      const previousTemplate = previous?.find((prevTemplate) => prevTemplate.key === template.key);

      queryClient.setQueryData<MessagingTemplate[]>(queryKey, (messaging) =>
        messaging?.map((item) => (item.key !== template.key ? item : { ...item, ...template }))
      );

      return { previous: previousTemplate };
    },
    onError: (error, queryData, onMutateResult, context) => {
      if (onMutateResult?.previous) {
        queryClient.setQueryData(queryKey, onMutateResult.previous);
      }

      options?.onError?.(error, queryData, onMutateResult, context);
    },
    onSettled: (queryData, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(queryKey, mutationKey);
      options?.onSettled?.(queryData, error, variables, onMutateResult, context);
    },
  });
};

export const useCreateMessagingForNewLocaleMutation = (
  companyKey: Company['key'],
  options?: PostCreateMessagingForNewLocaleOptions
) => {
  const queryKey = [MESSAGING_QUERY_KEY, LIST_KEY, companyKey];
  const localeListQueryKey = [MESSAGING_COMPANY_LOCALE_QUERY_KEY, LIST_KEY, companyKey];
  const mutationKey = [MESSAGING_QUERY_KEY, POST_KEY];

  return useMutation({
    mutationKey,
    ...options,
    mutationFn: postCreateMessagingForNewLocale,
    onSettled: () => {
      invalidateIfAllMutationsDone(queryKey, mutationKey);
      invalidateIfAllMutationsDone(localeListQueryKey, mutationKey);
    },
  });
};
